// Tiny formatters shared across pages.

export function formatBytes(bytes: number | undefined | null): string {
  if (bytes == null || bytes <= 0) return "—";
  const units = ["B", "KiB", "MiB", "GiB", "TiB", "PiB"];
  let idx = 0;
  let n = bytes;
  while (n >= 1024 && idx < units.length - 1) {
    n /= 1024;
    idx++;
  }
  return `${n.toFixed(n < 10 && idx > 0 ? 1 : 0)} ${units[idx]}`;
}

export function formatGB(gb: number | undefined | null): string {
  if (gb == null) return "—";
  return `${gb} GB`;
}

export function formatMB(mb: number | undefined | null): string {
  if (mb == null) return "—";
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb} MB`;
}

export function formatPrice(cents: number | undefined | null, currency = "USD"): string {
  if (cents == null) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

export function formatRelative(iso: string | undefined | null): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const diff = Date.now() - t;
  const abs = Math.abs(diff);
  const units: Array<[number, Intl.RelativeTimeFormatUnit]> = [
    [60_000, "second"],
    [3_600_000, "minute"],
    [86_400_000, "hour"],
    [86_400_000 * 7, "day"],
    [86_400_000 * 30, "week"],
    [86_400_000 * 365, "month"],
  ];
  let unit: Intl.RelativeTimeFormatUnit = "year";
  let divisor = 86_400_000 * 365;
  for (let i = 0; i < units.length; i++) {
    if (abs < units[i][0]) {
      unit = units[i][1];
      divisor = i === 0 ? 1000 : units[i - 1][0];
      break;
    }
  }
  const rtf = new Intl.RelativeTimeFormat(undefined, {numeric: "auto"});
  return rtf.format(Math.round(-diff / divisor), unit);
}

export function formatTimestamp(iso: string | undefined | null): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleString();
}

export type ServerStateVariant = "success" | "destructive" | "warning" | "muted" | "secondary";

export function serverStateVariant(state: string | undefined): ServerStateVariant {
  switch ((state ?? "").toLowerCase()) {
    case "running":
    case "active":
      return "success";
    case "suspended":
    case "error":
    case "failed":
      return "destructive";
    case "provisioning":
    case "starting":
    case "stopping":
    case "rebooting":
      return "warning";
    case "stopped":
    case "shutoff":
      return "muted";
    default:
      return "secondary";
  }
}
